import { groupBy } from 'lodash';
import { DirectSalesCampaignResponse } from 'components/common/types/DirectSalesCampaignResponse.types';
import { DealType } from 'components/common/types/Deal.types';
import {
  DirectSalesAllocationSolutionResponse,
  DirectSalesCampaignDealLineResponse,
  DirectSalesFilter,
  DirectSalesFilterGroupCriteria,
  DirectSalesFilterItem,
  DirectSalesFilterTypes,
  DirectSalesLine,
  DirectSalesLineObjective,
  DirectSalesMediaType,
  DirectSalesProductCategoryExclusivityType,
  DirectSalesStatusOption,
  DirectSalesState,
  DirectSalesTargetCriteria,
  TransformedDirectSalesBookingResponse,
  DirectSalesSegmentFilter,
} from 'components/common/types/DirectSalesCampaign.types';
import { PatternOption } from 'components/common/DaypartTargeting/DaypartTargeting.types';
import { LIVE_CAMPAIGN_STATUSES } from 'consts/campaignStatus';
import { DEFAULT_OBJECTIVE } from 'consts/objective';
import { DirectSalesExclusionFlag } from 'components/common/types/DirectSalesDeal.types';
import { POI } from 'components/common/types/Planner.types';
import { SelectedSegment } from 'components/common/types/Segment.types';
import { transformUnitListFiltersFromResponse } from './transformUnitListFiltersFromResponse';
import { reverseTransformDaypartTargeting } from './transformDirectSalesDaypartRangePattern';
import {
  mapLocationFilter,
  mappedFormatFilterName,
  mapPoiFilter,
  mapTagFilter,
  reverseTransformDealLineFilters,
} from './transformFilters';
import transformDirectSalesLineAllocation from './transformDirectSalesLineAllocation';
import { isDirectSalesLinePricedWithoutCancelled } from './getIsDirectSalesLinePriced';
import {
  transformObjectiveTarget,
  transformNetworkCriteria,
  transformTargetCriteria,
} from './transformDirectSalesTargetsAndObjectives';

export const transformLineStatus = (state: DirectSalesState, status: DirectSalesStatusOption): DirectSalesState => {
  if (!state?.status)
    return {
      status: DirectSalesStatusOption.DRAFT_UPDATING,
    };

  const isRecognizedState = Object.values(DirectSalesStatusOption).includes(state.status);

  return {
    status: LIVE_CAMPAIGN_STATUSES.includes(status) || !isRecognizedState ? status : state.status,
    ...(state.expires && state.status === DirectSalesStatusOption.OPTION ? { expires: new Date(state.expires) } : {}),
  };
};

export const transformObjectivesResponse = (
  objectivesData: DirectSalesTargetCriteria[] = [],
  lineStatus: DirectSalesStatusOption,
): DirectSalesLineObjective[] => {
  const groupedObjectives = groupBy(objectivesData, 'objectiveGroupId');

  // eslint-disable-next-line sonarjs/cognitive-complexity
  return Object.values(groupedObjectives).map((objectiveTargets) => {
    const [{ filter }] = objectiveTargets;
    const { listFiles, routeFrameCodes } = transformUnitListFiltersFromResponse(filter);

    return objectiveTargets.reduce(
      (acc, curr) => ({
        ...acc,
        ...(Number(curr.objectiveGroupId) ? { objectiveGroupId: Number(curr.objectiveGroupId) } : {}),
        ...(curr.name ? { name: curr.name } : {}),
        ...(curr.criteriaType === DirectSalesFilterGroupCriteria.BUDGET_TARGET
          ? { budget: transformObjectiveTarget(curr) }
          : {}),
        ...(curr.criteriaType === DirectSalesFilterGroupCriteria.FRAME_TARGET
          ? { frames: transformObjectiveTarget(curr) }
          : {}),
        ...(curr.criteriaType === DirectSalesFilterGroupCriteria.VOLUME_TARGET
          ? { impressions: transformObjectiveTarget(curr) }
          : {}),
        ...(filter ? { formats: reverseTransformDealLineFilters(filter, mappedFormatFilterName) } : { formats: [] }),
        ...(filter ? { locations: reverseTransformDealLineFilters(filter, mapLocationFilter) } : { locations: [] }),
        ...(filter ? { tags: reverseTransformDealLineFilters(filter, mapTagFilter) } : { tags: [] }),
        ...(filter ? { poi: reverseTransformDealLineFilters(filter, mapPoiFilter) as POI[] } : { poi: [] }),
        frameList: {
          listFiles,
          routeFrameCodes,
        },
        objectiveStatus: isDirectSalesLinePricedWithoutCancelled(lineStatus) ? curr.objectiveStatus : undefined,
      }),
      { ...DEFAULT_OBJECTIVE },
    );
  });
};

export const transformDirectSalesDealLineResponse = (
  dealLines: DirectSalesCampaignDealLineResponse[],
  campaignAllocation: DirectSalesAllocationSolutionResponse | null,
  campaignId?: string,
): Partial<DirectSalesLine>[] => {
  return dealLines.map(
    ({
      id: lineId,
      name: lineName,
      filter,
      range: { startDate, endDate, floatingDateRange },
      rangePattern,
      targetCriteria,
      periodType,
      weekStartDate,
      sweep,
      status,
      state,
      lockState,
      pricingMode,
      templateId,
      templateName,
      productLineReference,
      allocationFailures,
      // eslint-disable-next-line sonarjs/cognitive-complexity
    }) => {
      const { targetsData, objectivesData } = (targetCriteria ?? []).reduce(
        (
          acc: { targetsData: DirectSalesTargetCriteria[]; objectivesData: DirectSalesTargetCriteria[] },
          curr: DirectSalesTargetCriteria,
        ) => {
          if (curr.objectiveGroupId || curr.name || curr.filter) acc.objectivesData.push(curr);
          else acc.targetsData.push(curr);

          return acc;
        },
        { targetsData: [], objectivesData: [] },
      );

      const targets = transformTargetCriteria(targetsData);
      const networkCriteria = transformNetworkCriteria(targetsData);
      const objectives = transformObjectivesResponse(objectivesData, state.status);
      const allocation = transformDirectSalesLineAllocation(campaignAllocation, state.status, lineId, campaignId);

      const isWeeklyPattern = rangePattern?.type === 'WEEKLY';
      const patternRepeatType = isWeeklyPattern ? PatternOption.REPEAT_ONE_WEEK : PatternOption.NO_REPEAT;

      const foundMediaTypeFilter = filter?.filters.find(({ type }) => type === 'MediaTypeFilter');

      const mediaType =
        foundMediaTypeFilter && 'mediaType' in foundMediaTypeFilter
          ? (foundMediaTypeFilter.mediaType as DirectSalesMediaType)
          : undefined;

      const orFilters = filter?.filters.find(
        ({ type }) => type === DirectSalesFilterTypes.OrFilter,
      ) as DirectSalesFilter;

      const environments =
        orFilters?.filters
          .filter(({ type }) => type === 'ChannelFilter')
          .map((item: DirectSalesFilterItem) => ({
            code: item.id,
            name: item.name,
            audienceCategoryGroupCode: '',
            dayPartGroupCode: '',
          })) ?? [];

      const { listFiles, routeFrameCodes, mapRouteFrameCodes } = transformUnitListFiltersFromResponse(filter);

      const segmentFilter =
        ((filter?.filters ?? []) as Array<{ type: string; filters?: unknown[] }>)
          .flatMap(({ type, filters }) => (type === DirectSalesFilterTypes.OrFilter ? (filters ?? []) : []))
          .find((candidate): candidate is DirectSalesSegmentFilter => {
            if (candidate && typeof candidate === 'object' && 'type' in candidate) {
              const { type } = candidate as { type?: unknown };
              return typeof type === 'string' && type === 'SecondaryAudienceFilter';
            }
            return false;
          }) ?? null;

      const transformedSegment: SelectedSegment = segmentFilter
        ? (({ id, dataProvider, cpmPremium, cpmCurrency, cpmPercentage, name, minThreshold, maxThreshold }) => ({
            [id]: {
              dataProvider,
              segmentId: id,
              cpmPremium,
              cpmCurrency,
              cpmPercentage,
              segmentName: name,
              minThreshold,
              maxThreshold,
            },
          }))(segmentFilter)
        : {};

      return {
        lineId,
        name: lineName,
        startDate: startDate ? new Date(startDate) : null,
        endDate: endDate ? new Date(endDate) : null,
        impressions: targets.VOLUME_TARGET?.target,
        frames: targets.FRAME_TARGET?.target,
        budget: targets.BUDGET_TARGET?.target,
        sot: targets.SOT_TARGET?.target,
        sotObjectiveId: targets.SOT_TARGET?.objectiveId,
        budgetCriteria: targets.BUDGET_TARGET,
        framesCriteria: targets.FRAME_TARGET,
        impressionsCriteria: targets.VOLUME_TARGET,
        networkCriteria,
        objectives,
        environments,
        sweep,
        periodType,
        weekStartDate,
        state: transformLineStatus(state, status),
        patternLength: isWeeklyPattern ? 1 : 0,
        patternRepeatType,
        selectedDays: rangePattern ? reverseTransformDaypartTargeting(rangePattern, startDate, endDate) : {},
        lockState,
        pricingMode,
        dealLineFormats: filter ? reverseTransformDealLineFilters(filter, mappedFormatFilterName) : [],
        locations: filter ? reverseTransformDealLineFilters(filter, mapLocationFilter) : [],
        poi: filter ? (reverseTransformDealLineFilters(filter, mapPoiFilter) as POI[]) : [],
        tags: filter ? reverseTransformDealLineFilters(filter, mapTagFilter) : [],
        segment: {
          selectedSegmentDataProvider: segmentFilter?.dataProvider ?? '',
          selectedSegments: transformedSegment,
        },
        allocation,
        ...(mediaType ? { mediaType } : {}),
        routeFrameCodes,
        mapRouteFrameCodes,
        listFiles,
        uploadedFrameLists: listFiles,
        productTemplate: {
          id: templateId ?? '',
          name: templateName ?? '',
        },
        floatingRange: floatingDateRange,
        ...(productLineReference !== undefined ? { productLineReference } : {}),
        allocationFailures: allocationFailures ?? [],
      };
    },
  );
};

export const transformDirectSalesBookingResponse = ({
  campaign: {
    body,
    header: { id, userVisibleId, salesPerson, salesTeam, adminPerson, sessionLockStatus, enabledForSharing },
  },
}: DirectSalesCampaignResponse): TransformedDirectSalesBookingResponse => {
  const {
    campaignDefinition,
    campaignDefinition: {
      advertiserId,
      advertiserName,
      brandId,
      brandName,
      productCategory,
      productCategoryName,
      dealLines,
      status,
    },
    allocationSolution,
  } = body;

  const deal = {
    dealName: campaignDefinition.name || '',
    dealType: DealType.GUARANTEED,
    dealId: id,
    userVisibleId: userVisibleId ?? '',
    internalId: '',
    advertiser: {
      code: advertiserId,
      name: advertiserName,
    },
    brand: {
      code: brandId,
      name: brandName,
    },
    campaignStatus: status ?? DirectSalesStatusOption.DRAFT_UPDATING,
    duplicationExclusion: campaignDefinition.duplicationExclusion || DirectSalesExclusionFlag.ON,
    exclusions: campaignDefinition.exclusions || {
      productCategories: [],
      productCategoryGroups: [],
      organisations: [],
      indexedProductCategories: [],
    },
    juxtapositionExclusion: campaignDefinition.juxtapositionExclusion || DirectSalesExclusionFlag.ON,
    productCategory: {
      code: productCategory,
      name: productCategoryName,
    },
    productCategoryExclusivity:
      campaignDefinition.productCategoryExclusivity || DirectSalesProductCategoryExclusivityType.OFF,
    specialistBlockExclusion: campaignDefinition.specialistBlockExclusion || DirectSalesExclusionFlag.ON,
    agencyBlockExclusion: campaignDefinition.agencyBlockExclusion || DirectSalesExclusionFlag.ON,
    advertiserBlockExclusion: campaignDefinition.advertiserBlockExclusion || DirectSalesExclusionFlag.ON,
    ...(campaignDefinition.agencyName
      ? {
          agency: {
            code: campaignDefinition.agencyId,
            name: campaignDefinition.agencyName,
          },
        }
      : {}),
    ...(campaignDefinition.specialistName
      ? {
          specialist: {
            code: campaignDefinition.specialistId,
            name: campaignDefinition.specialistName,
          },
        }
      : {}),
    ...(salesPerson ? { salesPerson } : { salesPerson: null }),
    ...(salesTeam ? { salesTeam } : { salesTeam: null }),
    ...(adminPerson ? { adminPerson } : { adminPerson: null }),
  };

  const availability = {
    totalCost: 0,
    allocatedFrames: 0,
    allocatedImpressions: 0,
    availableImpressions: 0,
    availableFrames: 0,
    assets: [],
    unavailableAssets: [],
    missingAssets: [],
    assetCpmDistribution: { weightedMean: 0, min: 0, max: 0 },
  };

  const lines = transformDirectSalesDealLineResponse(dealLines, allocationSolution, id).map((line) => ({
    ...line,
    id,
    availability,
    enabledForSharing,
  }));

  return {
    deal,
    lines,
    sessionLockState: sessionLockStatus,
  };
};
